import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, randomBytes } from "@noble/hashes/utils.js";
import { base64 } from "@scure/base";

/**
 * Any content (PLAN.md 4.9). The time lock holds a JSON manifest: the text and, per file, the
 * key that opens its encrypted blob. Blobs live outside the event (R2), encrypted once with
 * their own AES-256-GCM key; without the manifest a blob is random bytes.
 *
 *   capsule 1041: content = tlock(manifest JSON), tag ["payload", "manifest-v1"]
 *   capsules without a payload tag are v1: the time-locked bytes are the text itself
 */
export const PAYLOAD_MANIFEST_V1 = "manifest-v1";

/** Pilot defaults; the server's MAX_ATTACHMENT_BYTES var is the authority on size. */
export const MAX_FILES = 10;
export const DEFAULT_MAX_ATTACHMENT_BYTES = 256 * 1024;
/** AES-GCM adds a 16-byte tag: a blob is its file's size + 16. */
export const BLOB_OVERHEAD = 16;

export type ManifestFile = {
  /** As the sender named it. Readers clean it before use (PLAN.md 4.11 H10). */
  name: string;
  /** MIME type as the sender's browser reported it. Readers never trust it for display. */
  type: string;
  /** Bytes before encryption. */
  size: number;
  /** Hex SHA-256 of the encrypted blob: its address on the server and its integrity check. */
  sha256: string;
  /** base64, 32 bytes */
  key: string;
  /** base64, 12 bytes */
  nonce: string;
};

export type Manifest = { v: 1; text: string; files: ManifestFile[] };

export class ManifestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ManifestError";
  }
}

const utf8 = new TextEncoder();

export function encodeManifest(text: string, files: ManifestFile[] = []): string {
  return JSON.stringify({ v: 1, text, files } satisfies Manifest);
}

/** Bytes the manifest takes inside the time lock: what the Compose counter and the limits measure. */
export function manifestBytes(text: string, files: ManifestFile[] = []): number {
  return utf8.encode(encodeManifest(text, files)).length;
}

function decodedLength(b64: unknown): number {
  if (typeof b64 !== "string") return -1;
  try {
    return base64.decode(b64).length;
  } catch {
    return -1;
  }
}

/** Validates a decrypted manifest. Unknown fields are ignored; known ones must be well formed. */
export function parseManifest(json: string): Manifest {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new ManifestError("manifest is not JSON");
  }
  if (typeof raw !== "object" || raw === null) throw new ManifestError("manifest must be an object");
  const m = raw as Record<string, unknown>;
  if (m.v !== 1) throw new ManifestError("unsupported manifest version");
  if (typeof m.text !== "string") throw new ManifestError("manifest text must be a string");
  if (!Array.isArray(m.files) || m.files.length > MAX_FILES) throw new ManifestError(`manifest holds at most ${MAX_FILES} files`);
  const files = m.files.map((f, i): ManifestFile => {
    const bad = (why: string) => new ManifestError(`file ${i + 1}: ${why}`);
    if (typeof f !== "object" || f === null) throw bad("not an object");
    const e = f as Record<string, unknown>;
    if (typeof e.name !== "string" || e.name.length > 255) throw bad("name");
    if (typeof e.type !== "string" || e.type.length > 255) throw bad("type");
    if (!Number.isSafeInteger(e.size) || (e.size as number) < 0) throw bad("size");
    if (typeof e.sha256 !== "string" || !/^[0-9a-f]{64}$/.test(e.sha256)) throw bad("sha256");
    if (decodedLength(e.key) !== 32) throw bad("key");
    if (decodedLength(e.nonce) !== 12) throw bad("nonce");
    return { name: e.name, type: e.type, size: e.size as number, sha256: e.sha256, key: e.key as string, nonce: e.nonce as string };
  });
  return { v: 1, text: m.text, files };
}

const subtle = () => {
  const c = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  if (!c) throw new Error("WebCrypto is not available in this environment");
  return c;
};

const toBuffer = (bytes: Uint8Array): ArrayBuffer => bytes.slice().buffer as ArrayBuffer;

export function blobHash(blob: Uint8Array): string {
  return bytesToHex(sha256(blob));
}

/** Encrypts one file with a fresh key. Upload `blob`; put `entry` in the manifest. */
export async function encryptFile(bytes: Uint8Array, meta: { name: string; type: string }): Promise<{ blob: Uint8Array; entry: ManifestFile }> {
  const key = randomBytes(32);
  const nonce = randomBytes(12);
  const aes = await subtle().importKey("raw", toBuffer(key), { name: "AES-GCM" }, false, ["encrypt"]);
  const blob = new Uint8Array(await subtle().encrypt({ name: "AES-GCM", iv: toBuffer(nonce) }, aes, toBuffer(bytes)));
  return {
    blob,
    entry: { name: meta.name, type: meta.type, size: bytes.length, sha256: blobHash(blob), key: base64.encode(key), nonce: base64.encode(nonce) },
  };
}

/** Checks the blob's SHA-256 against the manifest, then decrypts. Any change to the blob fails. */
export async function decryptFile(blob: Uint8Array, entry: ManifestFile): Promise<Uint8Array> {
  if (blobHash(blob) !== entry.sha256) throw new ManifestError(`${entry.name}: the downloaded blob does not match the capsule`);
  const aes = await subtle().importKey("raw", toBuffer(base64.decode(entry.key)), { name: "AES-GCM" }, false, ["decrypt"]);
  try {
    return new Uint8Array(await subtle().decrypt({ name: "AES-GCM", iv: toBuffer(base64.decode(entry.nonce)) }, aes, toBuffer(blob)));
  } catch {
    throw new ManifestError(`${entry.name}: the blob cannot be decrypted`);
  }
}

// ---------- safe display (PLAN.md 4.11 H10) ----------

/**
 * A file name from a manifest, made safe to use as a download name or archive path component:
 * no path separators, control or text-direction characters (U+202E can show "fdp.exe" as "exe.pdf"),
 * no leading dots, at most 100 characters.
 */
export function cleanFileName(name: string): string {
  let clean = name
    .normalize("NFC")
    .replace(/[\u0000-\u001f\u007f-\u009f\u200e\u200f\u2028-\u202e\u2066-\u2069]/g, "")
    .replace(/[\\/:*?"<>|]/g, "_")
    .trim()
    .replace(/^\.+/, "");
  if ([...clean].length > 100) {
    const dot = clean.lastIndexOf(".");
    const ext = dot > 0 && clean.length - dot <= 10 ? clean.slice(dot) : "";
    clean = [...clean].slice(0, 100 - [...ext].length).join("") + ext;
  }
  return clean || "file";
}

export type RasterImageType = "image/jpeg" | "image/png" | "image/gif" | "image/webp";

/**
 * The raster image type of these bytes, judged by their magic bytes, or null. Only these four are
 * ever shown inline; the type a sender declared in the manifest is never trusted for display.
 */
export function sniffImageType(bytes: Uint8Array): RasterImageType | null {
  const starts = (sig: number[], at = 0) => sig.every((b, i) => bytes[at + i] === b);
  if (starts([0xff, 0xd8, 0xff])) return "image/jpeg";
  if (starts([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "image/png";
  if (starts([0x47, 0x49, 0x46, 0x38]) && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61) return "image/gif";
  if (starts([0x52, 0x49, 0x46, 0x46]) && starts([0x57, 0x45, 0x42, 0x50], 8)) return "image/webp";
  return null;
}
